import { useState } from 'react';
import { CrossIcon } from 'assets/icons';
import { ReactComponent as FolderSourceIcon } from 'assets/images/folder-source.svg';
import { FolderHandling, SelectionShape } from 'helpers/upload-selection';
import { classnames } from 'helpers/utils';
import styles from './folder-question.module.scss';

interface FolderQuestionProps {
  shape: SelectionShape;
  // The album the upload was started from, if any: the folders can go into it.
  album?: { name: string; thumbnailUrl?: string | null };
  onBack: () => void;
  onClose: () => void;
  onContinue: (handling: FolderHandling) => void;
}

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

const AlbumChip = ({
  name,
  thumbnailUrl,
}: {
  name: string;
  thumbnailUrl?: string | null;
}) => (
  <span className={styles.albumChip}>
    <span className={styles.albumIcon}>
      <span className={styles.albumIconBack} />
      <span className={styles.albumIconFront}>
        {thumbnailUrl && <img src={thumbnailUrl} alt="" />}
      </span>
    </span>
    <span className={styles.albumChipName}>{name}</span>
  </span>
);

type Option = {
  value: FolderHandling;
  title: string;
  text: React.ReactNode;
  chips: { name: string }[];
};

// Shown only when the picked folders have subfolders (or several folders are
// added from inside an album): albums don't nest, so the user says whether
// the folders are combined or each becomes an album.
export const FolderQuestion = ({
  shape,
  album,
  onBack,
  onClose,
  onContinue,
}: FolderQuestionProps) => {
  const { groups, nested, totalFiles } = shape;
  // From the vault the usual answer is preselected; inside an album the user
  // has to say where the files belong.
  const [choice, setChoice] = useState<FolderHandling | null>(
    album ? null : 'albums',
  );

  // The question is about the one picked folder when it's added from the
  // vault, or when its subfolders have folders of their own. Otherwise it is
  // about the folders that could each become an album.
  const sourceFolder = !album || nested ? shape.sourceFolder : null;
  const combineChoice: FolderHandling = album ? 'loose' : 'oneAlbum';
  const allFiles = plural(totalFiles, 'file', 'files');
  const albumWord = groups.length === 1 ? 'Album' : 'Albums';

  let combine: Option;
  let separate: Option;
  if (!album) {
    combine = {
      value: combineChoice,
      title: 'Combine into 1 Album',
      text: `Put all ${allFiles} into a single Album.`,
      chips: [{ name: shape.sourceFolder?.name ?? '' }],
    };
    separate = {
      value: 'albums',
      title: `Create ${groups.length} ${albumWord}`,
      text: 'Each subfolder will become a separate Album in your Vault.',
      chips: groups,
    };
  } else if (sourceFolder) {
    combine = {
      value: combineChoice,
      title: `Add everything to “${album.name}” without subfolders`,
      text: `All ${allFiles} from this folder and its subfolders will be added to ${album.name}.`,
      chips: [{ name: album.name }],
    };
    separate = {
      value: 'albums',
      title: 'Make each subfolder a separate Album',
      text: 'Each folder will become a separate Album in your Vault.',
      chips: groups,
    };
  } else {
    combine = {
      value: combineChoice,
      title: `Add everything to “${album.name}”`,
      text: `All ${allFiles} from these folders will be added to ${album.name}.`,
      chips: [{ name: album.name }],
    };
    separate = {
      value: 'albums',
      title: `Create ${groups.length} new ${albumWord}`,
      text: (
        <>
          Each folder will become a separate Album in your Vault.
          <br />
          Albums will be side-by-side, not nested.
        </>
      ),
      chips: groups,
    };
  }
  const options = [combine, separate];

  return (
    <>
      <div className={styles.header}>
        {sourceFolder ? (
          <div className={styles.sourceFolder}>
            <FolderSourceIcon className={styles.folderIcon} />
            <div className={styles.sourceText}>
              <span className={styles.sourceName}>{sourceFolder.name}</span>
              <span>
                This folder has <strong>{allFiles}</strong> across{' '}
                <strong>
                  {plural(groups.length, 'subfolder', 'subfolders')}
                </strong>
                .
              </span>
            </div>
          </div>
        ) : (
          <div className={styles.sourceFolders}>
            {groups.map((group) => (
              <div className={styles.sourceFolder} key={group.name}>
                <FolderSourceIcon className={styles.folderIcon} />
                <div className={styles.sourceText}>
                  <span className={styles.sourceName}>{group.name}</span>
                  <span>{plural(group.fileCount, 'file', 'files')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <h2 id="upload-modal-title" className={styles.title}>
          {sourceFolder
            ? `How should “${sourceFolder.name}” be added?`
            : 'Where should these folders go?'}
        </h2>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          <CrossIcon />
        </button>
      </div>

      <div className={styles.options} role="radiogroup">
        {options.map((option) => {
          const selected = choice === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={classnames(styles.option, {
                [styles.optionSelected]: selected,
              })}
              onClick={() => setChoice(option.value)}
            >
              <span
                className={classnames(styles.radio, {
                  [styles.radioOn]: selected,
                })}
              />
              <span className={styles.optionBody}>
                <span className={styles.optionTitle}>{option.title}</span>
                <span className={styles.optionText}>{option.text}</span>
                <span className={styles.chips}>
                  {option.chips.map((chip) => (
                    <AlbumChip key={chip.name} name={chip.name} />
                  ))}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.footer}>
        {/* Only once the user has chosen to add to the open album. */}
        {album && choice === 'loose' && (
          <div className={styles.addingTo}>
            <span>Adding items to:</span>
            <AlbumChip name={album.name} thumbnailUrl={album.thumbnailUrl} />
          </div>
        )}
        <div className={styles.actions}>
          <button type="button" className={styles.back} onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className={styles.continue}
            disabled={!choice}
            onClick={() => choice && onContinue(choice)}
          >
            Continue
          </button>
        </div>
      </div>
    </>
  );
};
